/**
 * Transport-layer error mapping — TDD-v2 §2.5.
 *
 * Both `ElectronCrucibleClient` (IPC rejection path) and
 * `BrowserCrucibleClient` (JSON-RPC error envelope) emit a unified
 * `CrucibleError` exception so call sites in the renderer + browser
 * SPA see the same shape regardless of transport.
 *
 * Wire shape: the existing `SerialisedError` from
 * `@volley/crucible-ipc-types/error-types` already covers everything
 * the CLI's `CrucibleError.toJSON()` produces, AND the same shape is
 * what the cloud-VM's JSON-RPC server returns in `error.data` (per
 * `docs/cloud-vm/design/jsonrpc-channel.md` §2.5). So this module
 * binds to that one type instead of inventing a parallel envelope.
 *
 * Two entry points:
 *   - `wrap(error)` — call from `ElectronCrucibleClient.invoke`'s
 *     catch. Input is an `unknown` rejection from Electron's IPC
 *     boundary. Uses `unflattenIpcError` to decode the
 *     `STRUCTURED_IPC_ERROR_SENTINEL`-wrapped payload, then
 *     constructs the typed exception.
 *   - `fromJsonRpcError(envelope)` — call from
 *     `BrowserCrucibleClient.#onMessage` when a response carries
 *     `error: { code, message, data? }`. The `data` slot is a
 *     `SerialisedError` per the wire spec.
 */

import {
  CLOUD_RUNTIME_UPDATE_ERROR_CODE,
  CLOUD_RUNTIME_UPDATE_MESSAGE,
  CLOUD_RUNTIME_UPDATE_RECOVERY,
  type SerialisedError,
  unflattenIpcError,
} from '@volley/crucible-ipc-types';

/**
 * Renderer-side typed exception. Same shape on both transports so
 * call sites can `instanceof CrucibleError` and read `.code` /
 * `.category` / `.recovery` / `.retryable` without caring whether
 * the error came from Electron IPC or JSON-RPC over WSS.
 */
export class CrucibleError extends Error {
  readonly code?: string;
  readonly category: SerialisedError['category'];
  readonly recovery?: string;
  readonly retryable: boolean;
  readonly systemError?: SerialisedError['systemError'];
  readonly processOutput?: SerialisedError['processOutput'];
  readonly causePayload?: SerialisedError;
  /** Numeric JSON-RPC code when the error originated on the Cloud wire. */
  readonly rpcCode?: number;

  constructor(payload: SerialisedError, cause?: unknown, rpcCode?: number) {
    super(payload.message);
    this.name = 'CrucibleError';
    this.code = payload.code;
    this.category = payload.category;
    this.recovery = payload.recovery;
    this.retryable = payload.retryable;
    this.systemError = payload.systemError;
    this.processOutput = payload.processOutput;
    this.causePayload = payload.cause;
    this.rpcCode = rpcCode;
    if (cause !== undefined) {
      this.cause = cause;
    }
  }
}

/**
 * Decode an Electron IPC rejection (or any caught `unknown`) into a
 * `CrucibleError`. The underlying decoder is shared with the
 * pre-existing `<CrucibleErrorState>` renderer surface, so the
 * envelope semantics line up exactly.
 *
 * If the input is already a `CrucibleError`, return it unchanged —
 * idempotent so callers can `wrap` defensively without doubling
 * the message prefix or losing the original instance.
 */
export function wrap(error: unknown): CrucibleError {
  if (error instanceof CrucibleError) return error;
  const payload = unflattenIpcError(error);
  return new CrucibleError(payload, error);
}

/**
 * Construct a `CrucibleError` from a JSON-RPC 2.0 error envelope.
 *
 * The cloud-VM server emits errors per
 * `docs/cloud-vm/design/jsonrpc-channel.md` §2.5 — the wire shape is:
 *
 *   error.code:    JSON-RPC numeric code (-32xxx etc.)
 *   error.message: human-readable summary
 *   error.data: {
 *     crucibleCode: string  (e.g. "CRUCIBLE-700")
 *     category:     SerialisedError['category']
 *     shortName:    string  (slug identifier)
 *     recovery?:    string
 *     retryable:    boolean
 *     requestId?:   string  (server-side correlation id)
 *   }
 *
 * That's a DIFFERENT shape from the Electron IPC's `SerialisedError`
 * (which uses `code` + `name` + nested `cause`). We normalise the
 * JSON-RPC shape into a `SerialisedError` before constructing
 * `CrucibleError` so callers see a single typed exception shape
 * across both transports (codex round-1 P1).
 *
 * Fall through to a synthesised payload when `data` is absent or
 * doesn't match either of the two canonical shapes.
 */
export function fromJsonRpcError(envelope: {
  code: number;
  message: string;
  data?: unknown;
}): CrucibleError {
  // Prefer the JSON-RPC §2.5 shape (crucibleCode + shortName) when
  // recognisable — that's what the cloud-VM server actually emits.
  if (isJsonRpcErrorData(envelope.data)) {
    return new CrucibleError(
      {
        name: 'CrucibleError',
        message: envelope.message,
        code: envelope.data.crucibleCode,
        category: envelope.data.category,
        recovery: envelope.data.recovery,
        retryable: envelope.data.retryable,
      },
      undefined,
      envelope.code,
    );
  }
  // Defensive: a server that emits the IPC-style SerialisedError
  // (e.g. a cap-host that forwards the IPC envelope verbatim) still
  // surfaces correctly.
  if (isSerialisedError(envelope.data)) {
    return new CrucibleError(envelope.data, undefined, envelope.code);
  }
  return new CrucibleError(
    {
      name: 'CrucibleError',
      message: envelope.message,
      category: 'unknown',
      retryable: false,
    },
    undefined,
    envelope.code,
  );
}

/** Translate version skew only at a call site that requires the missing method. */
export function requiredCloudRuntimeError(error?: unknown): CrucibleError {
  return new CrucibleError(
    {
      name: 'CrucibleError',
      message: CLOUD_RUNTIME_UPDATE_MESSAGE,
      code: CLOUD_RUNTIME_UPDATE_ERROR_CODE,
      category: 'state',
      recovery: CLOUD_RUNTIME_UPDATE_RECOVERY,
      retryable: false,
    },
    error,
  );
}

interface JsonRpcErrorData {
  crucibleCode: string;
  category: SerialisedError['category'];
  shortName: string;
  recovery?: string;
  retryable: boolean;
  requestId?: string;
}

function isJsonRpcErrorData(value: unknown): value is JsonRpcErrorData {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.crucibleCode === 'string' &&
    typeof v.shortName === 'string' &&
    typeof v.category === 'string' &&
    typeof v.retryable === 'boolean'
  );
}

function isSerialisedError(value: unknown): value is SerialisedError {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.name === 'string' &&
    typeof v.message === 'string' &&
    typeof v.category === 'string' &&
    typeof v.retryable === 'boolean'
  );
}
